import { ATS, setActivePairBalance } from '../../features/ats/atsSlice';
import { store } from '../../app/store';
import {
  buildTradeBalanceParams,
  enrichWalletFromLinkedSiblings,
  enrichBalanceWithPairWallet,
  getLinkedWalletAddress,
  isNonCustodialWallet,
} from './linkedWallet.utils';
import { buildNonCustodialPairBalance } from './nonCustodialWallet.service';
import { errorNonCustodialBalance } from './nonCustodialBalance.debug';
import { invalidateReadCache } from './nonCustodialReadRpc';
import {
  getNonCustodialWeb3Session,
  isNonCustodialRpcBusy,
} from './nonCustodialSession';

/** Non-custodial settlement may lag on-chain — more follow-up reads. */
const NC_MATCH_FOLLOWUP_DELAYS_MS = [2000, 5000, 9000, 14000];
const NC_COMPLETED_FOLLOWUP_DELAYS_MS = [1500, 4000, 9000];

/** Custodial BitGo balances update via webhook/socket — fewer API polls. */
const CUSTODIAL_FOLLOWUP_DELAYS_MS = [2500, 7000];

const CUSTODIAL_BURST_DEBOUNCE_MS = 750;
const settlementRefreshTimers = new Map();

const balanceFetchCoalesce = {
  inFlightKey: '',
  inFlightPromise: null,
  debounceTimer: null,
  debounceWaiters: [],
};

const resolveBalanceFetchContext = ({ selectedPair, selectedTradeWallet, linkedWallets }) => {
  const state = store.getState()?.ats ?? {};
  const resolvedLinkedWallets = linkedWallets?.length
    ? linkedWallets
    : Array.isArray(state.linkedWallets)
      ? state.linkedWallets
      : [];

  const enrichedWallet =
    selectedTradeWallet && resolvedLinkedWallets.length
      ? enrichWalletFromLinkedSiblings(selectedTradeWallet, resolvedLinkedWallets)
      : selectedTradeWallet;

  return {
    selectedPair,
    selectedTradeWallet: enrichedWallet,
    linkedWallets: resolvedLinkedWallets,
  };
};

const buildBalanceFetchKey = (ctx) => {
  const { selectedPair, selectedTradeWallet } = resolveBalanceFetchContext(ctx);
  const walletKey =
    selectedTradeWallet?.walletId ??
    getLinkedWalletAddress(selectedTradeWallet) ??
    '';
  return [
    walletKey,
    selectedPair?.baseCurrency ?? '',
    selectedPair?.otherCurrency ?? '',
    isNonCustodialWallet(selectedTradeWallet) ? 'nc' : 'custodial',
  ].join('|');
};

const resolveFollowupDelays = (ctx, completed = false) => {
  const { selectedTradeWallet } = resolveBalanceFetchContext(ctx);
  if (selectedTradeWallet && isNonCustodialWallet(selectedTradeWallet)) {
    return completed ? NC_COMPLETED_FOLLOWUP_DELAYS_MS : NC_MATCH_FOLLOWUP_DELAYS_MS;
  }
  return CUSTODIAL_FOLLOWUP_DELAYS_MS;
};

/** True when socket/API order payload includes on-chain fill fields (settlement occurred). */
export const isSettlementFillOrderUpdate = (order) => {
  if (!order || typeof order !== 'object') return false;

  const filled = Number(order.filled_amount ?? order.filledAmount);
  const executed = order.executed_price ?? order.executedPrice;

  if (!Number.isFinite(filled) || filled <= 0) return false;
  if (executed == null || String(executed).trim() === '') return false;

  return true;
};

/** True for USER_TRADE / TRADE_ORDERS socket payloads (match event, buy or sell). */
export const isUserTradeSocketEvent = (order) => {
  if (!order || typeof order !== 'object') return false;
  if (isSettlementFillOrderUpdate(order)) return true;

  const filled = Number(
    order.filled_amount ?? order.filledAmount ?? order.quantity ?? order.qty,
  );
  const price =
    order.executed_price ??
    order.executedPrice ??
    order.price ??
    order.limit_price ??
    order.limitPrice;

  if (!Number.isFinite(filled) || filled <= 0) return false;
  if (price == null || String(price).trim() === '') return false;

  return true;
};

/** True when socket order payload indicates the order is fully filled / completed. */
export const isOrderFullyCompleted = (order) => {
  if (!order || typeof order !== 'object') return false;

  const status = Number(order.status);
  if (status === 3) return true;

  const quantity = Number(order.quantity ?? order.qty);
  const filled = Number(order.filled_amount ?? order.filledAmount);
  if (
    Number.isFinite(quantity) &&
    quantity > 0 &&
    Number.isFinite(filled) &&
    filled >= quantity
  ) {
    return true;
  }

  return false;
};

const scheduleDelayedRefresh = (dispatch, ctx, delaysMs) => {
  const pairKey = ctx.selectedPair?.pair_key ?? 'default';
  const pending = settlementRefreshTimers.get(pairKey);
  if (pending?.length) {
    pending.forEach((timerId) => clearTimeout(timerId));
  }

  const timers = delaysMs.map((delayMs) =>
    setTimeout(() => {
      const state = store.getState()?.ats ?? {};
      refreshActivePairBalance(
        dispatch,
        {
          selectedPair: ctx.selectedPair ?? state.selectedPair,
          selectedTradeWallet: ctx.selectedTradeWallet ?? state.selectedTradeWallet,
          linkedWallets: ctx.linkedWallets,
        },
        {
          forceRefresh: true,
          retryIfBusy: true,
          coalesce: true,
        },
      );
    }, delayMs),
  );
  settlementRefreshTimers.set(pairKey, timers);
};

const performRefreshActivePairBalance = async (
  dispatch,
  ctx,
  { forceRefresh = true, retryIfBusy = false } = {},
) => {
  const { selectedPair, selectedTradeWallet, linkedWallets } =
    resolveBalanceFetchContext(ctx);

  if (!selectedPair?.baseCurrency || !selectedPair?.otherCurrency) {
    return null;
  }

  if (selectedTradeWallet && isNonCustodialWallet(selectedTradeWallet)) {
    if (isNonCustodialRpcBusy()) {
      if (retryIfBusy) {
        setTimeout(
          () =>
            refreshActivePairBalance(
              dispatch,
              ctx,
              { forceRefresh, retryIfBusy: false, coalesce: false },
            ),
          600,
        );
      }
      return null;
    }

    const web3 = getNonCustodialWeb3Session();
    if (!web3) {
      if (retryIfBusy) {
        setTimeout(
          () =>
            refreshActivePairBalance(
              dispatch,
              ctx,
              { forceRefresh, retryIfBusy: false, coalesce: false },
            ),
          600,
        );
      } else {
        setTimeout(
          () =>
            refreshActivePairBalance(
              dispatch,
              ctx,
              { forceRefresh, retryIfBusy: true, coalesce: false },
            ),
          450,
        );
      }
      return null;
    }

    if (forceRefresh) {
      invalidateReadCache();
    }

    try {
      const balance = await buildNonCustodialPairBalance({
        web3,
        wallet: selectedTradeWallet,
        selectedPair,
        ownerAddress: getLinkedWalletAddress(selectedTradeWallet),
        linkedWallets,
      });
      if (balance) {
        const state = store.getState()?.ats ?? {};
        dispatch(
          setActivePairBalance(
            enrichBalanceWithPairWallet(
              balance,
              ctx.selectedPair ?? state.selectedPair,
              ctx.selectedTradeWallet ?? state.selectedTradeWallet,
              ctx.linkedWallets ?? state.linkedWallets,
            ),
          ),
        );
      }
      return balance;
    } catch (error) {
      errorNonCustodialBalance('refreshActivePairBalance:failed', {
        message: error?.message,
        chainId: selectedTradeWallet?.chainId,
      });
      return null;
    }
  }

  if (!selectedTradeWallet) {
    return null;
  }

  return dispatch(
    ATS.fetchUserBalance(
      buildTradeBalanceParams({
        baseCurrency: selectedPair.baseCurrency,
        otherCurrency: selectedPair.otherCurrency,
        selectedTradeWallet,
      }),
    ),
  );
};

/**
 * Refresh Accounts / activePairBalance for the current pair and wallet.
 * Non-custodial: on-chain read via MetaMask session (never custodial get_balance API).
 * Custodial: GET /wallet/v1/get_balance with optional walletId.
 *
 * Custodial bursts (place order + USER_TRADE socket) are debounced and in-flight deduped.
 */
export const refreshActivePairBalance = async (
  dispatch,
  ctx,
  { forceRefresh = true, retryIfBusy = false, coalesce = true } = {},
) => {
  const key = buildBalanceFetchKey(ctx);
  const { selectedTradeWallet } = resolveBalanceFetchContext(ctx);
  const isCustodial =
    Boolean(selectedTradeWallet) && !isNonCustodialWallet(selectedTradeWallet);

  const execute = () => {
    if (
      coalesce &&
      balanceFetchCoalesce.inFlightPromise &&
      balanceFetchCoalesce.inFlightKey === key
    ) {
      return balanceFetchCoalesce.inFlightPromise;
    }

    balanceFetchCoalesce.inFlightKey = key;
    balanceFetchCoalesce.inFlightPromise = performRefreshActivePairBalance(
      dispatch,
      ctx,
      { forceRefresh, retryIfBusy },
    ).finally(() => {
      if (balanceFetchCoalesce.inFlightKey === key) {
        balanceFetchCoalesce.inFlightKey = '';
        balanceFetchCoalesce.inFlightPromise = null;
      }
    });

    return balanceFetchCoalesce.inFlightPromise;
  };

  if (!coalesce || !isCustodial) {
    return execute();
  }

  return new Promise((resolve, reject) => {
    balanceFetchCoalesce.debounceWaiters.push({ resolve, reject });

    if (balanceFetchCoalesce.debounceTimer) {
      clearTimeout(balanceFetchCoalesce.debounceTimer);
    }

    balanceFetchCoalesce.debounceTimer = setTimeout(async () => {
      balanceFetchCoalesce.debounceTimer = null;
      const waiters = balanceFetchCoalesce.debounceWaiters.splice(0);

      try {
        const result = await execute();
        waiters.forEach((waiter) => waiter.resolve(result));
      } catch (error) {
        waiters.forEach((waiter) => waiter.reject(error));
      }
    }, CUSTODIAL_BURST_DEBOUNCE_MS);
  });
};

const refreshWithDelayedFollowUp = (dispatch, ctx, completed = false) => {
  refreshActivePairBalance(dispatch, ctx, { forceRefresh: true, coalesce: true });
  scheduleDelayedRefresh(dispatch, ctx, resolveFollowupDelays(ctx, completed));
};

/**
 * Settlement confirmed (ACTIVE_ORDERS with fill fields) — safe to read chain immediately.
 */
export const refreshActivePairBalanceOnSettlementConfirmed = (dispatch, ctx) => {
  refreshWithDelayedFollowUp(dispatch, ctx, true);
};

/**
 * Match / TRADE_ORDERS — settlement may still be in flight; refresh now and follow up.
 */
export const refreshActivePairBalanceAfterSettlement = (dispatch, ctx) => {
  refreshWithDelayedFollowUp(dispatch, ctx, false);
};

/** Order fully completed on socket — immediate refresh plus short follow-ups. */
export const refreshActivePairBalanceOnOrderCompleted = (dispatch, ctx) => {
  refreshWithDelayedFollowUp(dispatch, ctx, true);
};

export default refreshActivePairBalance;
